import { useEffect } from "react";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, BRAND_KEYWORDS } from "@/lib/seo";

function upsertMeta(attr, key, content) {
    if (!content) return;
    let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
}

// SEO helper — sets title, description, keywords, canonical, Open Graph,
// Twitter cards and JSON-LD structured data for rich results + AI search.
export default function Seo({
    title,
    description,
    keywords,
    image = DEFAULT_OG_IMAGE,
    path = "",
    type = "website",
    jsonLd,
}) {
    useEffect(() => {
        if (title) document.title = title;
        const absImage = image?.startsWith("http") ? image : SITE_URL + image;
        const url = SITE_URL + path;

        upsertMeta("name", "description", description);
        upsertMeta("name", "keywords", keywords || BRAND_KEYWORDS);
        upsertMeta("name", "author", "Abhijit JK");
        upsertMeta("name", "robots", "index, follow, max-image-preview:large");

        // Canonical
        let canon = document.head.querySelector('link[rel="canonical"]');
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", url);

        // Open Graph
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:type", type);
        upsertMeta("property", "og:url", url);
        upsertMeta("property", "og:image", absImage);
        upsertMeta("property", "og:site_name", SITE_NAME);

        // Twitter
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", title);
        upsertMeta("name", "twitter:description", description);
        upsertMeta("name", "twitter:image", absImage);

        // JSON-LD
        const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
        document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((s) => s.remove());
        blocks.forEach((b) => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-seo-jsonld", "1");
            s.text = JSON.stringify(b);
            document.head.appendChild(s);
        });

        return () => {
            document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((s) => s.remove());
        };
    }, [title, description, keywords, image, path, type, jsonLd]);

    return null;
}
